import {
  Smartphone, Globe, HandCoins, QrCode, CalendarCheck, Video,
  Users, Mail, Languages, LifeBuoy, ShieldCheck, HeartHandshake,
  TrendingUp, MessageSquare, Sparkles, Building2, CheckCircle2,
} from "lucide-react";

export const IMAGES = {
  heroCongregation:
    "https://images.unsplash.com/photo-1622598453695-4fbaf151aadc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  device:
    "https://images.unsplash.com/photo-1726137569807-c0ce211ccbec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  family:
    "https://images.unsplash.com/photo-1596510914965-9ae08acae566?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  unity:
    "https://images.unsplash.com/photo-1630068846062-3ffe78aa5049?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  community:
    "https://images.unsplash.com/photo-1742659655744-3504f1152644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
};

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Solutions", href: "#solutions" },
  { label: "Giving Device", href: "#giving" },
  { label: "How It Works", href: "#how" },
  { label: "Packages", href: "#packages" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const LANG_OPTIONS = [
  { code: "EN", label: "English", active: true },
  { code: "KR", label: "Kreyòl", active: true },
  { code: "FR", label: "Français", active: false },
  { code: "ES", label: "Español", active: false },
];

export const SOLUTIONS = [
  { icon: Smartphone, title: "In-Person Giving Devices", desc: "A simple, familiar way to give during services." },
  { icon: Globe, title: "Professional Websites", desc: "A clean, welcoming, mobile-friendly church website." },
  { icon: HandCoins, title: "Online Giving", desc: "Secure giving from anywhere, any day of the week." },
  { icon: QrCode, title: "Donation QR Codes", desc: "Tap-to-give codes for bulletins, screens, and events." },
  { icon: CalendarCheck, title: "Events & Registration", desc: "Publish events and collect sign-ups with ease." },
  { icon: Video, title: "Livestream & Sermons", desc: "Bring worship and past messages online." },
  { icon: Users, title: "Visitor Forms", desc: "Welcome guests and follow up with care." },
  { icon: Mail, title: "Member Communication", desc: "Email and newsletters that keep everyone connected." },
  { icon: Languages, title: "Multilingual Options", desc: "Serve your church in English, Kreyòl, French, or Spanish." },
  { icon: LifeBuoy, title: "Ongoing Support", desc: "A partner to call when you have no tech staff." },
];

export const STEPS = [
  { n: "01", title: "Free Consultation", desc: "We learn about your church and its needs." },
  { n: "02", title: "Personalized Recommendation", desc: "We recommend the right solution for your ministry and budget." },
  { n: "03", title: "Setup", desc: "We coordinate approved services and build your digital solution." },
  { n: "04", title: "Launch & Support", desc: "We launch, train your team, and provide support when needed." },
];

export const PACKAGES = [
  {
    name: "Essential",
    price: "$997",
    tagline: "An affordable way to establish or improve your church's digital giving and online presence.",
    popular: false,
    cta: "Get Started — $997",
    features: [
      "Payment-provider application assistance",
      "Eligible giving-device setup assistance",
      "Essential church website",
      "Online-giving connection",
      "Donation QR code",
      "Contact & prayer-request form",
      "Social-media connections",
      "Basic training & initial website support",
    ],
  },
  {
    name: "Growth",
    price: "$1,497",
    tagline: "A stronger, more complete digital system for growing ministries.",
    popular: true,
    cta: "Request a Free Consultation",
    inherits: "Everything in Essential, plus:",
    features: [
      "Expanded website",
      "Events & registration",
      "Visitor connection forms",
      "Livestream & sermon integration",
      "Newsletter signup",
      "Website analytics",
      "Additional training & support",
    ],
  },
  {
    name: "Complete",
    price: "$2,497",
    tagline: "An advanced digital presence and ongoing ministry technology.",
    popular: false,
    cta: "Build My Church Solution",
    inherits: "Everything in Growth, plus:",
    features: [
      "Larger website",
      "Multilingual options",
      "Multiple campaign pages",
      "Advanced event registration",
      "Volunteer & membership forms",
      "Automated visitor follow-up",
      "Appointment / pastoral-request system",
      "Advanced analytics & priority support",
    ],
  },
];

export const CARE_PLANS = [
  {
    name: "Foundation Care",
    price: "$149",
    per: "/month",
    includes: [
      "Software updates & backups",
      "Security monitoring",
      "Up to 2 content updates / month",
      "Email support · 2 business-day response",
    ],
    excludes: "New pages, redesigns, or integrations are quoted separately.",
  },
  {
    name: "Growth Care",
    price: "$299",
    per: "/month",
    includes: [
      "Everything in Foundation Care",
      "Up to 5 content updates / month",
      "Event & form updates + testing",
      "Monthly analytics summary",
      "Email & text support · 1 business-day response",
    ],
    excludes: "Does not include large redesigns or new integrations.",
  },
  {
    name: "Ministry Management",
    price: "$599",
    per: "/month",
    includes: [
      "Everything in Growth Care",
      "Up to 10 content updates / month",
      "Priority support · same business-day response",
      "Communication assistance",
      "Quarterly strategy check-in",
    ],
    excludes: "Custom development is scoped separately. Plans are not unlimited.",
  },
];

export const WHY = [
  "We understand church culture and ministry needs",
  "Personal, human service — never a call center",
  "One coordinated partner for giving and websites",
  "Support for churches with no dedicated tech staff",
];

export const GIVING_STEPS = [
  { icon: HandCoins, label: "Choose Amount" },
  { icon: Smartphone, label: "Tap Card or Phone" },
  { icon: CheckCircle2, label: "Donation Confirmed" },
];

export const FAQS = [
  { q: "How does approval for a payment device work?", a: "Merchant approval, equipment, and contractual terms are determined by participating third-party payment-processing providers. We assist you through the application, but final approval is made by the provider." },
  { q: "What are the processing rates and transaction fees?", a: "Processing rates, transaction fees, deposits, and chargebacks are set and handled by the third-party provider — not by Konek Church. We help you understand and complete the setup." },
  { q: "How long does a website take?", a: "It depends on your package and how quickly content is provided. Most projects launch within a few weeks. We give a clear estimate during your consultation." },
  { q: "Do we own our website?", a: "Yes. Your church's website and content belong to your church. Domain, hosting, and third-party software may carry their own separate terms and costs." },
  { q: "Can our website support multiple languages?", a: "Yes. Our Complete package supports multilingual options, and we serve churches in English, Kreyòl, French, and Spanish." },
  { q: "What does monthly support include?", a: "Each care plan lists exactly what's included, the update allowance, support method, and response times — plus what's not included. Plans are not unlimited." },
  { q: "Who handles device and transaction issues?", a: "The payment-processing provider handles merchant approval, equipment, transactions, deposits, chargebacks, processing rates, and device-related technical support. We help connect you with the right resources." },
];

export const INTEREST_OPTIONS = [
  "Giving device",
  "Church website",
  "Online giving",
  "Complete digital solution",
  "Not sure — help me choose",
];

export const LANGUAGES = ["English", "Kreyòl", "Français", "Español"];

// Only methods with a real value are shown on the site.
export const CONTACT = {
  email: "hello@konekchurch.com",
  phone: "+19548256471",
  whatsapp: "",
  sms: "+19548256471",
};

export const FOUNDER = {
  name: "Abner Sergilles",
  role: "Founder, Konek Church",
  initials: "AS",
  bio: "I started Konek Church to help ministries adopt practical digital tools without pastors having to become technology experts. We speak your language, respect your mission, and stay with you after launch.",
};

// Set url to a hosted MP4 clip to activate the demo player. Empty = "coming soon" poster.
export const DEMO_VIDEO = { url: "", poster: "", captions: "" };

// Founder media — set photo/video URLs when available; placeholders shown until then.
export const FOUNDER_MEDIA = { photo: "", video: "", videoPoster: "", videoCaptions: "" };

// Populate ONLY with real, permission-granted testimonials. Empty = none shown.
export const TESTIMONIALS = [];

export const MARQUEE_ITEMS = [
  "Built for Ministry", "Secure Giving", "Multilingual Support",
  "One Connected Partner", "Care for Your Congregation",
];

export { Sparkles };
